import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatearFechaHora, type Permiso } from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { Aviso, Cargando, Etiqueta, Vacio } from '../componentes/piezas.js';
import { EscanerQr, hayCamara } from '../componentes/camara.js';
import { useEsMovil } from '../servicios/responsive.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

// ─── Actividad reciente (§14.5) ──────────────────────────────────────────────

interface Actividad {
  id: string;
  entidad: string;
  entidadId: string;
  tipoEvento: string;
  usuario: string | null;
  fechaHora: string;
  texto: string | null;
  referencia: string | null;
}

const NOMBRE_ENTIDAD: Record<string, string> = {
  ORDEN_TRABAJO: 'Orden de trabajo',
  SOLICITUD: 'Solicitud',
  ACTIVO: 'Activo',
  UBICACION: 'Ubicación',
  USUARIO: 'Usuario',
  GRUPO: 'Grupo',
  TIPO_FALLA: 'Tipo de falla',
  CATALOGO: 'Catálogo',
};

export function ActividadReciente(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['actividad'],
    queryFn: () => api.get<Actividad[]>('/actividad?limite=100'),
    refetchInterval: 30_000,
  });

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Actividad Reciente</h1>
          <p>Feed cronológico de toda la organización, transversal a los módulos.</p>
        </div>
      </div>

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? <Vacio titulo="Sin actividad registrada" /> : null}

        {data && data.length > 0 ? (
          <div className="panel-cuerpo">
            <div className="hilo">
              {data.map((evento) => (
                <article key={evento.id} className="hilo-evento">
                  <div className="hilo-marca" aria-hidden="true">
                    {(evento.usuario ?? '·').charAt(0).toUpperCase()}
                  </div>
                  <div className="hilo-cuerpo">
                    <div className="hilo-meta">
                      {evento.usuario ?? 'Sistema'} · {formatearFechaHora(evento.fechaHora)} ·{' '}
                      {NOMBRE_ENTIDAD[evento.entidad] ?? evento.entidad}
                    </div>
                    <div className="hilo-texto">
                      {evento.entidad === 'ORDEN_TRABAJO' ? (
                        <EnlaceRuta to={`/ordenes/${evento.entidadId}`}>{evento.referencia ?? 'Orden de trabajo'}</EnlaceRuta>
                      ) : evento.entidad === 'SOLICITUD' ? (
                        <Link to={`/solicitudes/${evento.entidadId}`}>{evento.referencia ?? 'Solicitud'}</Link>
                      ) : (
                        (evento.referencia ?? evento.texto ?? '—')
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

// ─── Notificaciones (§15.2) ──────────────────────────────────────────────────

interface Notificacion {
  id: string;
  tipo: string;
  titulo: string;
  cuerpo: string | null;
  entidad: string | null;
  entidadId: string | null;
  leidaEl: string | null;
  creadaEl: string;
}

export function Notificaciones(): JSX.Element {
  const cliente = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notificaciones'],
    queryFn: () => api.get<Notificacion[]>('/notificaciones'),
  });

  const marcar = useMutation({
    mutationFn: () => api.post('/notificaciones/leer', {}),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['notificaciones'] }),
  });

  const noLeidas = (data ?? []).filter((n) => !n.leidaEl).length;

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Notificaciones</h1>
          <p>Asignaciones, aprobaciones y alertas, en un solo lugar.</p>
        </div>
        {noLeidas > 0 ? (
          <button type="button" className="boton secundario" onClick={() => marcar.mutate()}>
            Marcar todas como leídas
          </button>
        ) : null}
      </div>

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? <Vacio titulo="Sin notificaciones" /> : null}

        {data && data.length > 0 ? (
          <div className="panel-cuerpo">
            <div className="hilo">
              {data.map((n) => (
                <article key={n.id} className={`hilo-evento${n.leidaEl ? '' : ' comentario'}`}>
                  <div className="hilo-marca" aria-hidden="true">
                    {n.leidaEl ? '·' : '●'}
                  </div>
                  <div className="hilo-cuerpo">
                    <div className="hilo-meta">{formatearFechaHora(n.creadaEl)}</div>
                    <div className="hilo-texto" style={{ fontWeight: n.leidaEl ? 400 : 700 }}>
                      {n.entidad === 'ORDEN_TRABAJO' && n.entidadId ? (
                        <EnlaceRuta to={`/ordenes/${n.entidadId}`}>{n.titulo}</EnlaceRuta>
                      ) : n.entidad === 'SOLICITUD' && n.entidadId ? (
                        <Link to={`/solicitudes/${n.entidadId}`}>{n.titulo}</Link>
                      ) : (
                        n.titulo
                      )}
                    </div>
                    {n.cuerpo ? (
                      <div style={{ fontSize: 12.5, color: 'var(--texto-medio)' }}>{n.cuerpo}</div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

// ─── Escaneo de QR (§16.2) ───────────────────────────────────────────────────

interface Resolucion {
  entidad: 'UBICACION' | 'ACTIVO' | 'PARTE';
  entidadId: string;
  nombre: string;
  detalle: string | null;
  ubicacionId: string | null;
}

const RUTA_POR_ENTIDAD: Record<Resolucion['entidad'], string> = {
  ACTIVO: '/activos',
  UBICACION: '/ubicaciones',
  PARTE: '/partes',
};

/** Permiso de lectura que exige la ficha completa de cada entidad — para no
 *  ofrecer «Ver ficha» a quien no puede abrirla (ej. un Solicitante). */
const PERMISO_LECTURA_POR_ENTIDAD: Record<Resolucion['entidad'], Permiso> = {
  ACTIVO: 'activo:leer',
  UBICACION: 'ubicacion:leer',
  PARTE: 'parte:leer',
};

/**
 * Resolución de código QR.
 *
 * En el móvil, lo primero que hace falta es la cámara apuntando a la
 * etiqueta — no un formulario para escribir (§ pedido): la cámara se abre
 * sola al entrar, y el código manual queda como respaldo detrás de un enlace,
 * para cuando la etiqueta está gastada o no hay cámara. En escritorio, sin
 * cámara a mano casi nunca, el formulario manual sigue siendo lo primero que
 * se ve.
 */
export function EscanearQr(): JSX.Element {
  const { puede } = useSesion();
  const esMovil = useEsMovil();
  const conCamara = hayCamara();
  const [codigo, setCodigo] = useState('');
  const [resultado, setResultado] = useState<Resolucion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [escaneando, setEscaneando] = useState(() => esMovil && conCamara);
  const [mostrarManual, setMostrarManual] = useState(() => !(esMovil && conCamara));

  const resolver = useMutation({
    mutationFn: (valor: string) => api.get<Resolucion>(`/qr/${encodeURIComponent(valor)}`),
    onSuccess: (datos) => {
      setResultado(datos);
      setError(null);
    },
    onError: (fallo: unknown) => {
      setResultado(null);
      setError(fallo instanceof ErrorApi ? fallo.message : 'No se pudo resolver el código.');
    },
  });

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Escanear QR</h1>
          <p>Un solo espacio de códigos para ubicaciones, activos y partes.</p>
        </div>
      </div>

      <div className="panel" style={{ maxWidth: 560 }}>
        <div className="panel-cuerpo">
          {esMovil ? (
            // Móvil: la cámara es la acción principal (§ pedido) — el código a
            // mano queda detrás de un enlace, para la etiqueta gastada o el
            // dispositivo sin cámara.
            <>
              {conCamara ? (
                <button
                  type="button"
                  className="boton"
                  style={{ width: '100%', marginBottom: 12 }}
                  onClick={() => setEscaneando(true)}
                >
                  Escanear con cámara
                </button>
              ) : null}

              {!mostrarManual ? (
                <button type="button" className="boton-enlace" onClick={() => setMostrarManual(true)}>
                  O ingresar el código a mano
                </button>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (codigo.trim()) resolver.mutate(codigo.trim().toUpperCase());
                  }}
                >
                  <div className="campo">
                    <label htmlFor="codigo">Código</label>
                    <input
                      id="codigo"
                      value={codigo}
                      onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                      placeholder="Ej.: QNM0V66UVLMM4"
                      maxLength={13}
                      style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em' }}
                      autoFocus={!conCamara}
                    />
                  </div>
                  <button type="submit" className="boton secundario" disabled={!codigo.trim() || resolver.isPending}>
                    Resolver
                  </button>
                </form>
              )}
            </>
          ) : (
            // Escritorio: sin cámara a mano casi nunca, el código sigue siendo
            // lo primero, con «Escanear con cámara» como acción secundaria.
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (codigo.trim()) resolver.mutate(codigo.trim().toUpperCase());
              }}
            >
              <div className="campo">
                <label htmlFor="codigo">Código</label>
                <input
                  id="codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                  placeholder="Ej.: QNM0V66UVLMM4"
                  maxLength={13}
                  style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em' }}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="boton" disabled={!codigo.trim() || resolver.isPending}>
                  Resolver
                </button>
                {conCamara ? (
                  <button type="button" className="boton secundario" onClick={() => setEscaneando(true)}>
                    Escanear con cámara
                  </button>
                ) : null}
              </div>
            </form>
          )}

          {escaneando ? (
            <EscanerQr
              alCerrar={() => setEscaneando(false)}
              alDetectar={(valor) => {
                setEscaneando(false);
                setCodigo(valor);
                resolver.mutate(valor);
              }}
              // Si la cámara falla, el modal ya muestra su propio campo manual
              // (`mostrarManual || error` en `EscanerQr`) — acá no hace falta
              // pedirlo de entrada porque, al cerrar, esta misma página ofrece
              // el código a mano detrás del enlace «O ingresar el código a mano».
              mostrarManual={false}
            />
          ) : null}

          {error ? (
            <div style={{ marginTop: 14 }}>
              <Aviso tono="error">{error}</Aviso>
            </div>
          ) : null}

          {resultado ? (
            <div style={{ marginTop: 16 }}>
              <Etiqueta tono="azul">
                {resultado.entidad === 'ACTIVO' ? 'Activo' : resultado.entidad === 'UBICACION' ? 'Ubicación' : 'Parte'}
              </Etiqueta>
              <h2 style={{ margin: '10px 0 4px', fontSize: 18 }}>
                {puede(PERMISO_LECTURA_POR_ENTIDAD[resultado.entidad]) ? (
                  <Link
                    to={`${RUTA_POR_ENTIDAD[resultado.entidad]}/${resultado.entidadId}`}
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    {resultado.nombre}
                  </Link>
                ) : (
                  resultado.nombre
                )}
              </h2>
              {resultado.detalle ? (
                <p style={{ margin: 0, color: 'var(--texto-medio)' }}>{resultado.detalle}</p>
              ) : null}
              {/* Una PARTE no tiene ubicación propia ni tiene sentido como origen de
                  una solicitud/OT: solo ofrece ir a su ficha. Y cada acción depende
                  del rol — un solicitante ve «Crear solicitud» pero no «Crear OT».
                  Apiladas y de ancho completo, con la acción principal primero:
                  lo que trajo al usuario acá es reportar/generar trabajo, no mirar
                  la ficha. */}
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {resultado.entidad !== 'PARTE' && puede('ot:crear') ? (
                  <Link
                    to={`/ordenes/nueva${consulta({
                      activoId: resultado.entidad === 'ACTIVO' ? resultado.entidadId : undefined,
                      ubicacionId:
                        resultado.entidad === 'UBICACION' ? resultado.entidadId : (resultado.ubicacionId ?? undefined),
                      titulo: `Incidencia en ${resultado.nombre}`,
                    })}`}
                    className="boton pequeno"
                  >
                    Crear orden de trabajo
                  </Link>
                ) : null}
                {resultado.entidad !== 'PARTE' && puede('solicitud:crear') ? (
                  <Link
                    to={`/solicitudes/nueva${consulta({
                      activoId: resultado.entidad === 'ACTIVO' ? resultado.entidadId : undefined,
                      ubicacionId:
                        resultado.entidad === 'UBICACION' ? resultado.entidadId : (resultado.ubicacionId ?? undefined),
                      titulo: `Incidencia en ${resultado.nombre}`,
                    })}`}
                    className="boton secundario pequeno"
                  >
                    Crear solicitud
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
